import {
  activeConditionsQuery,
  activeMedicationRequestsQuery,
  ageYears,
  conditionsMatching,
  earliestAuthoredOn,
  latestObservation,
  medicationRequestsIn,
  patientOf,
  type ContextResources,
} from '../planner/context';
import type { ProtocolSpec } from '../planner/plan';
import type { ProtocolVariables } from '../planner/types';
import { CODE_SYSTEMS } from '@liza/fhir-utils';
import type { Coding } from '@medplum/fhirtypes';
import { insulinsValueSet, statinsValueSet, sulfonylureasValueSet } from '../valueSets/rxNormDrugClasses';
import { t2dmOngoingMeasurements } from './t2dmOngoingMeasurements';
import { t2dmRoutineMonitoring, T2DM_CODES } from './t2dmRoutineMonitoring';

/**
 * The codings that mark high-risk feet for the foot-exam cadence: diabetic neuropathy, diabetic foot
 * ulcer, and acquired absence of toe, foot or leg.
 *
 * Both systems, because a diagnosis reaches the chart in whichever its source used. ICD-10-CM
 * matches as a prefix (`E11.4` covers the category); SNOMED matches exactly, so each concept is
 * named rather than a parent whose descendants we would have to resolve.
 *
 * Every SNOMED code here was checked against the International Edition via a terminology `$lookup`.
 * Acquired absence of toe/foot/leg has no SNOMED entry for that reason — the candidates did not
 * survive the check — so an amputation coded only in SNOMED still reads as low-risk. Adding those
 * concepts needs a verified lookup, not a plausible identifier: an unverified code never matches and
 * fails exactly as silently as the ICD-10-only read this replaces.
 */
export const FOOT_HIGH_RISK_CODINGS: readonly Coding[] = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'E11.4' },
  { system: CODE_SYSTEMS.ICD10_CM, code: 'E11.62' },
  { system: CODE_SYSTEMS.ICD10_CM, code: 'Z89.4' },
  { system: CODE_SYSTEMS.ICD10_CM, code: 'Z89.5' },
  { system: CODE_SYSTEMS.SNOMED, code: '49455004', display: 'Polyneuropathy due to diabetes mellitus' },
  { system: CODE_SYSTEMS.SNOMED, code: '230572002', display: 'Neuropathy due to diabetes mellitus' },
  { system: CODE_SYSTEMS.SNOMED, code: '371087003', display: 'Diabetic foot ulcer' },
];
/** Binds exactly the variables `T2DM_CONTEXT_VARIABLES` declares (a test pins the two lists to each other). */
export const deriveT2dmVariables = (resources: ContextResources, now: Date): ProtocolVariables => {
  const statins = medicationRequestsIn(resources, statinsValueSet);
  const hypoglycemiaCapable = [
    ...medicationRequestsIn(resources, insulinsValueSet),
    ...medicationRequestsIn(resources, sulfonylureasValueSet),
  ];
  return {
    '%a1c': { kind: 'resource', value: latestObservation(resources, CODE_SYSTEMS.LOINC, T2DM_CODES.A1C) },
    '%ldl': { kind: 'resource', value: latestObservation(resources, CODE_SYSTEMS.LOINC, T2DM_CODES.LDL) },
    '%ageYears': { kind: 'integer', value: ageYears(patientOf(resources), now) },
    '%onStatin': { kind: 'boolean', value: statins.length > 0 },
    '%statinStartedAt': { kind: 'dateTime', value: earliestAuthoredOn(statins) },
    '%onInsulinOrSulfonylurea': { kind: 'boolean', value: hypoglycemiaCapable.length > 0 },
    '%footHighRisk': {
      kind: 'boolean',
      value: conditionsMatching(resources, FOOT_HIGH_RISK_CODINGS).length > 0,
    },
  };
};

/** The type 2 diabetes protocol, ready for `planProtocol`. */
export const t2dmProtocol: ProtocolSpec = {
  planDefinition: t2dmRoutineMonitoring,
  valueSets: [statinsValueSet, insulinsValueSet, sulfonylureasValueSet],
  queries: [activeMedicationRequestsQuery, activeConditionsQuery],
  deriveVariables: deriveT2dmVariables,
};

/**
 * The ongoing-measurement protocol, ready for `planProtocol`. No variables: every action is
 * unconditional, so nothing needs deriving — but `activeConditionsQuery` is still listed, because
 * topic matching reads the patient's Conditions to find the topic this protocol belongs on.
 */
export const t2dmOngoingMeasurementsProtocol: ProtocolSpec = {
  planDefinition: t2dmOngoingMeasurements,
  valueSets: [],
  queries: [activeConditionsQuery],
  deriveVariables: () => ({}),
};
